import { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, PartyPopper, Loader2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const EMPTY = {
  group_name: "",
  name: "",
  email: "",
  phone: "",
  party_size: "10",
  occasion: "",
  notes: "",
};

/**
 * Inline group-RSVP form — same submission contract as ReservationDialog
 * (mode=group) but rendered directly on the page instead of in a modal.
 */
export default function BirthdayRSVPForm() {
  const [form, setForm] = useState(EMPTY);
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!date) {
      toast.error("Please pick a date");
      return;
    }
    setSubmitting(true);
    try {
      const { getReferralCode } = await import("@/lib/referral");
      await api.post("/reservations", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        party_size: parseInt(form.party_size, 10) || 1,
        date: format(date, "yyyy-MM-dd"),
        time: null,
        occasion: form.occasion || "Birthday / Group",
        notes: form.notes || null,
        booking_type: "group",
        group_name: form.group_name || null,
        referrer_code: getReferralCode() || null,
      });
      toast.success("Got it. We'll call to confirm your party.");
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div
        data-testid="birthday-form-done"
        className="border border-[var(--trio-primary)] bg-[var(--trio-primary)]/5 p-10 text-center"
      >
        <PartyPopper className="w-10 h-10 text-[var(--trio-primary)] mx-auto mb-4" />
        <h3 className="font-display uppercase text-3xl tracking-tight mb-3">
          You're on the list 🎉
        </h3>
        <p className="text-[var(--trio-muted)] leading-relaxed max-w-md mx-auto">
          Someone from Trio will call {form.phone || "you"} within a couple hours
          to lock in your night. Check your email for a confirmation from{" "}
          <strong>hello@triohsv.com</strong>.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="birthday-rsvp-form"
      className="space-y-5"
    >
      <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
        <PartyPopper className="w-3.5 h-3.5" />
        Group RSVP
      </div>

      <FieldRow
        label="Group / Celebrant Name"
        value={form.group_name}
        onChange={(v) => update("group_name", v)}
        placeholder="e.g. Jasmine's 30th"
        testid="bd-group-name"
      />

      <FieldRow
        label="Your Full Name"
        value={form.name}
        onChange={(v) => update("name", v)}
        required
        testid="bd-name"
      />

      <FieldRow
        label="Email"
        type="email"
        value={form.email}
        onChange={(v) => update("email", v)}
        required
        placeholder="We'll send your confirmation here"
        testid="bd-email"
      />

      <FieldRow
        label="Phone"
        value={form.phone}
        onChange={(v) => update("phone", v)}
        required
        placeholder="So we can call or text to confirm"
        testid="bd-phone"
      />

      <div>
        <Label className="text-xs tracking-widest uppercase">Date</Label>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="bd-date-trigger"
              className="w-full mt-2 flex items-center gap-2 border border-[var(--trio-border)] bg-[var(--trio-bg)] px-3 py-2 text-left text-sm"
            >
              <CalendarIcon className="w-4 h-4 text-[var(--trio-primary)]" />
              {date ? format(date, "EEE, MMM d, yyyy") : (
                <span className="text-[var(--trio-muted)]">Choose a date</span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="p-0 bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none"
          >
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      <FieldRow
        label="How Many People"
        type="number"
        value={form.party_size}
        onChange={(v) => update("party_size", v)}
        required
        testid="bd-party-size"
      />

      <FieldRow
        label="Occasion (optional)"
        value={form.occasion}
        onChange={(v) => update("occasion", v)}
        placeholder="Birthday, engagement, promotion…"
        testid="bd-occasion"
      />

      <div>
        <Label className="text-xs tracking-widest uppercase">
          Anything we should know?
        </Label>
        <Textarea
          value={form.notes}
          onChange={(e) => update("notes", e.target.value)}
          placeholder="Cake, dietary needs, playlist requests, decor plans…"
          rows={3}
          data-testid="bd-notes"
          className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="bd-submit"
        className="w-full inline-flex items-center justify-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-6 py-4 text-sm tracking-widest uppercase font-bold disabled:opacity-60 transition-colors"
      >
        {submitting ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <PartyPopper className="w-5 h-5" />
        )}
        {submitting ? "Sending…" : "Send Group RSVP"}
      </button>
    </form>
  );
}

function FieldRow({ label, value, onChange, required, type = "text", placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs tracking-widest uppercase">{label}</Label>
      <Input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
      />
    </div>
  );
}
